<ng-container *ngIf="vm$ | async as vm">
  @if (visible(vm)) {
    <section
      class="copilot-summary font-13 pt-2 pb-2 border-bottom"
      [class.copilot-summary--no-score]="!fits(380)"
      [class.copilot-summary--no-labels]="!fits(330)"
      [class.copilot-summary--no-signals]="!fits(300)"
      [class.copilot-summary--wrap]="!fits(260)"
      [attr.aria-label]="'attendance.copilot.summary.ariaLabel' | translate"
    >
      @if (vm.insight.view; as insightView) {
        @if (showInsight(vm.insight)) {
          <div class="copilot-summary__row copilot-summary__row--insight d-flex align-items-center gap-2">
            <i class="ri-lightbulb-line font-15 text-primary flex-shrink-0"></i>
            <span class="font-12 fw-medium text-dark flex-shrink-0">{{ "attendance.copilot.insight.title" | translate }}</span>

            @if (insightView.insight; as insight) {
              <span class="badge campaign-badge flex-shrink-0" [ngClass]="riskTone(insightView)">
                <span class="campaign-badge-dot"></span>{{ riskKey(insightView) | translate }}
              </span>
              <!-- The signals give way as the panel narrows: the score first, then the new messages (scss). -->
              <small class="copilot-summary__signals text-muted text-truncate">
                <span class="copilot-summary__score">{{ insight.content.relationship.health.score }}/100</span>
                @if (vm.insight.newMessages > 0) {
                  <span class="copilot-summary__score"> · </span>
                  <span>{{ newMessagesKey(vm.insight.newMessages) | translate: { count: vm.insight.newMessages } }}</span>
                }
              </small>
            } @else if (generating(vm.insight)) {
              <small class="copilot-summary__status text-muted text-truncate">{{ "attendance.copilot.insight.generating" | translate }}</small>
            } @else if (insightFailed(vm.insight)) {
              <small class="copilot-summary__status text-muted text-truncate">
                <i class="ri-error-warning-line me-1"></i>{{ "attendance.copilot.insight.generateFailed" | translate }}
              </small>
            }

            <span class="ms-auto d-inline-flex align-items-center gap-1 flex-shrink-0">
              @if (!closed(insightView)) {
                <!-- Icon only (spec M-16): the tooltip names the action, or says why it is not available yet. -->
                <span
                  class="d-inline-block"
                  [matTooltip]="requestKey(vm.insight) | translate"
                  matTooltipPosition="above"
                >
                  <button
                    type="button"
                    class="btn btn-link p-0 copilot-summary__icon-btn"
                    [disabled]="!canRequest(vm.insight)"
                    [attr.aria-label]="requestKey(vm.insight) | translate"
                    (click)="generate()"
                  >
                    @if (!generating(vm.insight)) {
                      <i class="ri-refresh-line font-16"></i>
                    } @else {
                      <mat-spinner [diameter]="14" mode="indeterminate" />
                    }
                  </button>
                </span>
              }
              <button
                type="button"
                class="btn btn-link p-0 ps-1 font-12 fw-medium text-decoration-none"
                [attr.aria-label]="'attendance.copilot.summary.open' | translate"
                (click)="open.emit('insight')"
              >
                <span class="copilot-summary__label">{{ "attendance.copilot.summary.open" | translate }}</span
                ><i class="ri-arrow-right-s-line align-middle"></i>
              </button>
            </span>
          </div>

          @if (waiting(vm.insight)) {
            <small class="text-muted d-block">{{ waitingKey(insightView) | translate: { n: insightView.waiting?.n } }}</small>
          }
        }
      }

      @if (vm.actions.view; as actionsView) {
        @if (showActions(vm.actions)) {
          <div class="copilot-summary__row d-flex align-items-center gap-2">
            <i class="ri-list-check-2 font-15 text-primary flex-shrink-0"></i>
            <span class="font-12 fw-medium text-dark flex-shrink-0">{{ "attendance.copilot.actions.title" | translate }}</span>
            @if (hasActions(actionsView)) {
              <span class="campaign-chip flex-shrink-0">
                {{ countKey(pending(actionsView).length) | translate: { count: pending(actionsView).length } }}
              </span>
            }
            @if (actionsGenerating(actionsView)) {
              <small class="copilot-summary__status text-muted d-inline-flex align-items-center gap-1 text-truncate" role="status">
                <mat-spinner [diameter]="14" mode="indeterminate" />{{ "attendance.copilot.summary.updating" | translate }}
              </small>
            } @else if (actionsFailed(actionsView)) {
              <small class="copilot-summary__status text-muted text-truncate">
                <i class="ri-error-warning-line me-1"></i>{{ "attendance.copilot.actions.generateFailed" | translate }}
              </small>
            }
            <button
              type="button"
              class="btn btn-link p-0 ps-1 ms-auto flex-shrink-0 font-12 fw-medium text-decoration-none"
              [attr.aria-label]="'attendance.copilot.summary.viewAll' | translate"
              (click)="open.emit('actions')"
            >
              <span class="copilot-summary__label">{{ "attendance.copilot.summary.viewAll" | translate }}</span
              ><i class="ri-arrow-right-s-line align-middle"></i>
            </button>
          </div>

          <!-- The most important pending action, markable in place by the responsible attendant (RF-15, RF-35);
               with no actions yet, why (RF-33). -->
          @if (next(actionsView); as item) {
            <div class="copilot-summary__next d-flex align-items-center gap-2 rounded bg-light-subtle px-2 py-1">
              @if (markable(actionsView)) {
                <mat-checkbox class="copilot-summary__check flex-grow-1" [checked]="isMarking(vm.actions, item)" (change)="markDone(item)">
                  <span class="d-block text-truncate text-dark">{{ item.action }}</span>
                </mat-checkbox>
              } @else {
                <i
                  class="ri-checkbox-blank-circle-line font-16 text-muted"
                  role="img"
                  [attr.aria-label]="'attendance.copilot.actions.state.pending' | translate"
                ></i>
                <span class="flex-grow-1 text-truncate text-dark">{{ item.action }}</span>
              }
              <span class="campaign-chip flex-shrink-0" [class.is-brand]="item.owner === 'supervisor'">
                {{ "attendance.copilot.actions.owner." + item.owner | translate }}
              </span>
            </div>
          } @else if (actionsNotGenerated(actionsView)) {
            <small class="text-muted d-block">{{ "attendance.copilot.actions.notGenerated" | translate }}</small>
          }
        }
      }
    </section>
  }
</ng-container>
